import React, { useId, useState } from "react";

import { css } from "@emotion/react";

import { SerializedStyles } from "@emotion/react";
import { CSS_COLORS } from "../../../styles/styles-css-variables";

import cn from "classnames";
import { fontColor, FONTS, fontSize } from "../../../styles/fonts";

type InputProps = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;
type LabelProps = React.DetailedHTMLProps<React.LabelHTMLAttributes<HTMLLabelElement>, HTMLLabelElement>;

type Variant = "green" | "outline";

type SwarovskiRadioProps = React.PropsWithChildren<{
  inputProps: InputProps;
  labelProps: LabelProps;
  customColorInput?: string;
  hasError?: boolean;
  type: "radio" | "checkbox";
  variant?: Variant;
  attachStyles?: SerializedStyles;
  className?: string;
}>;

export const SwarovskiRadio: React.FC<SwarovskiRadioProps> = ({ variant: variant_, ...props }) => {
  const id = useId();
  const variant: Variant = variant_ ?? "green";
  const disabledClassName = props.inputProps.disabled ? "disabled" : undefined;

  return (
    <div
      className={cn(props.className, `variant-${variant}`, `type-${props.type}`, disabledClassName)}
      css={[
        css`
          border: none;
          display: block;
          padding: 0;
          margin: 0;
        `,
        ...styles,
        props.attachStyles,
      ]}
    >
      <label {...props.labelProps} htmlFor={id}>
        <input
          {...props.inputProps}
          type={props.type}
          onChange={props.inputProps.onChange}
          onKeyDown={props.inputProps.onKeyDown}
          id={id}
          className={cn(props.customColorInput ? "custom-color" : undefined, props.hasError ? "has-error" : undefined)}
          css={css`
            --custom-color: ${props.customColorInput};
          `}
        />

        <span className={"_label"}>{props.children}</span>
      </label>
    </div>
  );
};

const styles = [
  FONTS.SwarovskiFutura300,
  fontColor.black,
  fontSize.x2,
  css`
    display: flex;

    &:not(.custom-color) {
      &.variant-green {
        --color: ${CSS_COLORS.Green};
        --text-color: ${CSS_COLORS.Black};
        --selected-color: ${CSS_COLORS.Green};
      }

      &.variant-outline {
        --color: ${CSS_COLORS.White};
        --text-color: ${CSS_COLORS.White};
        --selected-color: ${CSS_COLORS.White};
      }

      &.disabled {
        filter: opacity(0.5);
      }
    }

    input {
      &.custom-color {
        --color: var(--custom-color);
      }
    }

    --d: 8px;
    label {
      display: flex;
      gap: 8px;
    }

    align-items: center;

    cursor: pointer;
    label {
      cursor: pointer;
    }
    &.disabled {
      cursor: not-allowed;

      label {
        cursor: not-allowed;
      }
    }

    input:focus:focus-visible {
      outline: 1px dotted var(--text-color) !important;
    }

    @supports (-webkit-appearance: none) or (-moz-appearance: none) {
      input[type="checkbox"],
      input[type="radio"] {
        --real-width: 22px;

        --fake-width: calc(var(--real-width));
        --fake-height: var(--real-width);

        flex-shrink: 0;

        --border-width: 2px;

        -webkit-appearance: none;
        -moz-appearance: none;

        outline: none;
        border: none; // iOS
        display: inline-block;
        vertical-align: top;
        position: relative;
        margin: 0;
        cursor: pointer;

        width: calc(var(--fake-width));
        height: var(--fake-height);

        --size: var(--fake-height);

        &.has-error {
          &:before {
            border: var(--border-width) solid ${CSS_COLORS.RedCustom};
          }
        }

        &.rectangle {
          &::before {
            border-radius: 4px;
          }
        }

        &.custom-color {
          &:before {
            background-color: var(--color);
          }

          &[disabled] {
            &:before {
              border-color: transparent;
              filter: opacity(0.3);
            }
          }

          &:checked {
            &:before {
              box-shadow: 1px 1px 8px 2px rgb(56 56 56 / 33%);
              border-color: transparent;
            }
          }
        }

        &[disabled] {
          cursor: not-allowed;

          &:before {
            border: 2px solid ${CSS_COLORS.Grey};
          }

          & + label {
            color: ${CSS_COLORS.Grey};
            cursor: not-allowed;
          }
        }
      }

      input[type="checkbox"] {
        border: 2px solid var(--color);
        border-radius: 4px;

        &[disabled] {
        }

        &:checked {
          &:before,
          &:after {
            background-color: var(--color);

            content: "";
            position: absolute;
          }

          &:before {
            width: 8px;
            height: 3px;
            left: 1px;
            top: 10px;
            transform: rotate(45deg);
          }

          &:after {
            width: 14px;
            height: 3px;
            left: 4px;
            top: 8px;
            transform: rotate(-45deg);
          }
        }
      }

      input[type="radio"] {
        &:before {
          border-radius: 50%;
          content: "";
          position: absolute;

          width: calc(var(--size));
          height: calc(var(--size));
          box-sizing: border-box;

          left: 0;
          top: 0;

          border: var(--border-width) solid var(--color);
        }

        &:checked {
          &:after {
            content: "";
            position: absolute;

            background-color: var(--color);
            left: calc(0.5 * var(--d));
            top: calc(0.5 * var(--d));

            width: calc(var(--size) - var(--d));
            height: calc(var(--size) - var(--d));
            box-sizing: border-box;

            border: var(--border-width) solid var(--color);
            border-radius: 50%;
          }
        }
      }

      label {
        flex-grow: 1;

        color: var(--text-color);
        ${fontSize.x2};

        ._label {
          display: flex;
          align-items: center;

          margin-top: 2px;
          line-height: 1;
        }
      }
    }
  `,
];
